import React, { useEffect, useMemo, useState } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import {
  MULTI_DIM_FILTER_LOGIC_OPTIONS,
  MULTI_DIM_TEXT_FILTER_OPERATORS,
  createDefaultCompositeFilter,
} from "./multiDimensionColumnFilter.constants";
import { hasActiveFilterValue } from "./multiDimensionColumnFilterEval";
import MultiDimensionColumnFilterPopupShell from "./MultiDimensionColumnFilterPopupShell";
import { normalizeMultiDimensionType, VARIABLE_ICONS, WORKFLOW_VARIABLE_LABELS } from "../workflowVariables.constants";

const cloneComposite = (c) => ({
  logic: c.logic,
  filters: c.filters.map((f) => ({ ...f, value: f.value })),
});

/**
 * Text column filter: two operator + value rows, And/Or (Kendo-style).
 */
const MultiDimensionTextColumnFilterPopup = ({ dimension, anchorRef, initialComposite, onApply, onClear, onClose }) => {
  const type = normalizeMultiDimensionType(dimension?.type);
  const defaultC = useMemo(() => createDefaultCompositeFilter("text"), []);
  const [draft, setDraft] = useState(() => initialComposite ?? defaultC);

  useEffect(() => {
    setDraft(cloneComposite(initialComposite ?? defaultC));
  }, [dimension?.id, initialComposite, defaultC]);

  const operators = MULTI_DIM_TEXT_FILTER_OPERATORS;

  const updateRow = (index, patch) => {
    setDraft((prev) => {
      const filters = [...prev.filters];
      filters[index] = { ...filters[index], ...patch };
      return { ...prev, filters };
    });
  };

  const selectedLogic = MULTI_DIM_FILTER_LOGIC_OPTIONS.find((o) => o.value === draft.logic) ?? MULTI_DIM_FILTER_LOGIC_OPTIONS[0];

  const handleApply = () => {
    const anyActive = draft.filters.some((f) => hasActiveFilterValue(f.value, type));
    if (!anyActive) {
      onClear();
      return;
    }
    onApply({ ...draft, filters: draft.filters.map((f) => ({ ...f })) });
  };

  const handleClear = () => {
    setDraft(cloneComposite(defaultC));
    onClear();
  };

  const typeIcon = VARIABLE_ICONS.text;

  return (
    <MultiDimensionColumnFilterPopupShell
      anchorRef={anchorRef}
      typeIcon={typeIcon}
      onClose={onClose}
      onApply={handleApply}
      onClear={handleClear}
      applyDisabled={false}>
      <div className='d-flex flex-column gap-2'>
        <div className='d-flex flex-column gap-1'>
          <DropDownList
            className='w-100'
            data={operators}
            textField='text'
            dataItemKey='operator'
            value={operators.find((o) => o.operator === draft.filters[0]?.operator) ?? operators[0]}
            onChange={(e) => updateRow(0, { operator: e.value?.operator ?? operators[0].operator })}
          />
          <Input
            className='form-control form-control-sm w-100'
            value={draft.filters[0]?.value ?? ""}
            onChange={(e) => updateRow(0, { value: e.target.value })}
            placeholder={WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_TYPE_TEXT}
          />
        </div>
        <DropDownList
          className='w-50'
          data={MULTI_DIM_FILTER_LOGIC_OPTIONS}
          textField='label'
          dataItemKey='value'
          value={selectedLogic}
          onChange={(e) => setDraft((prev) => ({ ...prev, logic: e.value?.value ?? "and" }))}
        />
        <div className='d-flex flex-column gap-1'>
          <DropDownList
            className='w-100'
            data={operators}
            textField='text'
            dataItemKey='operator'
            value={operators.find((o) => o.operator === draft.filters[1]?.operator) ?? operators[0]}
            onChange={(e) => updateRow(1, { operator: e.value?.operator ?? operators[0].operator })}
          />
          <Input
            className='form-control form-control-sm w-100'
            value={draft.filters[1]?.value ?? ""}
            onChange={(e) => updateRow(1, { value: e.target.value })}
            placeholder={WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_TYPE_TEXT}
          />
        </div>
      </div>
    </MultiDimensionColumnFilterPopupShell>
  );
};

export default MultiDimensionTextColumnFilterPopup;
